import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/useToast";
import { useNavigate } from "react-router-dom";
import { UserPlus, ArrowLeft } from "lucide-react";
import { createEmployee } from "../api/employees";
import { useAuth } from "../contexts/AuthContext";
import { createOffice, getOffice, uploadLogo, uploadSignature } from "../api/officeandcompany";


const AddOffice = () => {
  const [loading, setLoading] = useState(false);
  const [employeeData, setEmployeeData] = useState({
    office_name: "",
    location: "",
    working_hours_start: "",
    working_hours_end: "",
    wifi_ssid: "",
    lattitude: null,
    longitude: null,
    radius_meters: null,
    is_active: true,
  });
  const { toast } = useToast();
  const { user } = useAuth();
  const isReadOnly = ["employee", "intern", "supervisor"].includes(user?.role || "");
  const navigate = useNavigate();


  const fetchEmployees = async () => {
    try {
      setLoading(true);
      console.log("Fetching employees...");
      const response = await getOffice();
    //   console.log(
    //     "Employees fetched successfully:",
    //     response.office?.length || 0
    //   );
      console.log(response.offices,'response.office[0]')
      setEmployeeData(
        response?.offices[0] || {
          office_name: "",
          location: "",
          working_hours_start: "",
          working_hours_end: "",
          wifi_ssid: "",
          lattitude: null,
          longitude: null,
          radius_meters: null,
          is_active: true,
        }
      );
    } catch (error) {
      console.error("Error fetching employees:", error);
      toast({
        // title: "Error",
        description: error.message || "Failed to load employees",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);


  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  if (isReadOnly) return; 

  try {

    setLoading(true);

    let logoUrl = "";
    let signatureUrl = "";

    // ✅ call TS API function
    if (employeeData.upload_logo) {
      const logoRes = await uploadLogo(employeeData.upload_logo);
      // New shape: { data: { logoPath, logoUrl } } | legacy: { url } / { path }
      logoUrl = logoRes?.data?.logoUrl ?? logoRes?.url ?? logoRes?.path ?? "";
    }

    // ✅ call TS API function
    if (employeeData.upload_signature) {
      const signRes = await uploadSignature(employeeData.upload_signature);
      signatureUrl = signRes?.data?.signatureUrl ?? signRes?.url ?? signRes?.path ?? "";
    }

    // The radius input is a text <Input>, so clearing it leaves "" in
    // state. Backend `radius_meters` is Optional[float] and rejects "".
    // Normalize empty / non-numeric values to null before sending.
    const rawRadius = (employeeData as any).radius_meters;
    const normalizedRadius =
      rawRadius === "" || rawRadius === null || rawRadius === undefined
        ? null
        : Number.isFinite(Number(rawRadius))
          ? Number(rawRadius)
          : null;

    // ✅ create office payload
    const payload = {
      ...employeeData,
      radius_meters: normalizedRadius,
      upload_logo: logoUrl,
      upload_signature: signatureUrl,
    };

    const response = await createOffice(payload);

    if (response.success) {

      fetchEmployees();

      toast({
        title: "Success",
        description: "Office added successfully",
      });

    }

  } catch (error: any) {

    toast({
      variant: "destructive",
      // title: "Error",
      description: error.message,
    });

  } finally {

    setLoading(false);

  }

};

  const handleInputChange = (field: string, value: string) => {
    console.log(`AddEmployee: ${field} field changed to:`, value);
    setEmployeeData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };
      console.log(`AddEmployee: Updated employee data:`, newData);
      return newData;
    });
  };

  if (loading) {
  return (
    <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
  );
}

  return (
    <div className="space-y-6 mx-4 my-8">
      {/* <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            console.log("AddEmployee: Back button clicked");
            navigate('/employees');
          }}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Employees
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Add New Employee</h1>
          <p className="text-muted-foreground">
            Create a new employee record in the system.
          </p>
        </div>
      </div> */}

      <Card className="max-w-1xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Office Information
          </CardTitle>
          <CardDescription>
            Enter the details for the new office. Fields marked with{" "}
            <span className="font-semibold">*</span> are required; the rest are
            optional but recommended for in-office attendance checks.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="name">Office Names *</Label>
                <Input
                  id="name"
                  value={employeeData.office_name}
                  onChange={(e) => {
                    handleInputChange("office_name", e.target.value);
                  }}
                  placeholder="Enter office name"
                  required
                  disabled={isReadOnly}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="working_hours_start">
                  Working Hours Start *
                </Label>
                <Input
                  id="working_hours_start"
                  type="time"
                  value={(employeeData.working_hours_start || "").slice(0, 5)}
                  onChange={(e) => {
                    const v = e.target.value;
                    const prev = (
                      employeeData.working_hours_start || ""
                    ).slice(0, 5);
                    handleInputChange("working_hours_start", v);
                    if (
                      v.length === 5 &&
                      prev.length === 5 &&
                      v.split(":")[1] !== prev.split(":")[1]
                    ) {
                      setTimeout(() => e.target.blur(), 0);
                    }
                  }}
                  required
                  disabled={isReadOnly}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="working_hours_end">Working Hours End *</Label>
                <Input
                  id="working_hours_end"
                  type="time"
                  value={(employeeData.working_hours_end || "").slice(0, 5)}
                  onChange={(e) => {
                    const v = e.target.value;
                    const prev = (
                      employeeData.working_hours_end || ""
                    ).slice(0, 5);
                    handleInputChange("working_hours_end", v);
                    if (
                      v.length === 5 &&
                      prev.length === 5 &&
                      v.split(":")[1] !== prev.split(":")[1]
                    ) {
                      setTimeout(() => e.target.blur(), 0);
                    }
                  }}
                  required
                  disabled={isReadOnly}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wifi_ssid">Wifi Name</Label>
                <Input
                  id="wifi_ssid"
                  value={employeeData.wifi_ssid}
                  onChange={(e) => {
                    handleInputChange("wifi_ssid", e.target.value);
                  }}
                  placeholder="Enter wifi name"
                  disabled={isReadOnly}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lattitude">Latitude</Label>
                <Input
                  id="lattitude"
                  value={employeeData.lattitude}
                  onChange={(e) => {
                    handleInputChange("lattitude", e.target.value);
                  }}
                  placeholder="Enter latitude"
                  disabled={isReadOnly}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="longitude">Longitude</Label>
                <Input
                  id="longitude"
                  value={employeeData.longitude}
                  onChange={(e) => {
                    handleInputChange("longitude", e.target.value);
                  }}
                  placeholder="Enter longitude"
                  disabled={isReadOnly}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="radius_meters">Radius Meters</Label>
                <Input
                  id="radius_meters"
                  value={employeeData.radius_meters}
                  onChange={(e) => {
                    handleInputChange("radius_meters", e.target.value);
                  }}
                  placeholder="Enter radius in meters"
                  disabled={isReadOnly}
                />
              </div>

            </div>

            <div className="flex gap-2 pt-4 justify-end">
              {!isReadOnly && (
              <Button type="submit" disabled={loading} style={{backgroundColor:'lightseagreen'}}>
                {loading ? (
                  "Creating..."
                ) : (
                  <>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Save Information
                  </>
                )}
              </Button>
              )}
             
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddOffice;
